import React, { useState, useEffect } from "react";
import { Drawer, Skeleton, Flex, Space, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { ShareSingleReport } from "./shareSingleReport";
import TrialTreeComponent from "./TrialReportTree";
import { type Report } from "./types";
import { formatTranslation } from "../../../shared/utils/translationUtils";
import { TrialReportAmplitudeEvent } from "../../../shared/utils/amplitudeEvents";
import { t } from "i18next";
import AxiosInstance from "../../../shared/utils/axios";
import { useAppState } from "../../../lib/appContext/AppContext";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";
import { useTrackingEvent } from "../../../shared/custom-hooks/useTracking";

interface TrialReportDrawerProps {
  open: boolean;
  onClose: () => void;
  trialId: string;
  fieldIds: string[];
  reportData?: {
    uuid: string | null;
    reportId: string | null;
  };
  onGenerateReport?: (
    trialId: string[],
    fieldIds: string[]
  ) => Promise<{ uuid: string; reportId: string }>;
  propertyId: string;
  noOfTrails: number | undefined;
  noOfFields: number | undefined;
  drawerView?: "share" | "tree";
  onEdit?: () => void;
  onTreeBack?: () => void;
  treeCheckedKeys?: string[];
  setTreeCheckedKeys?: (keys: string[]) => void;
}
export const TrialReportDrawer: React.FC<TrialReportDrawerProps> = ({
  open,
  onClose,
  trialId,
  fieldIds = [],
  reportData,
  onGenerateReport,
  propertyId,
  noOfTrails,
  noOfFields,
  drawerView,
  onEdit,
  onTreeBack,
  treeCheckedKeys,
  setTreeCheckedKeys,
}) => {
  const [currentReportUUID, setCurrentReportUUID] = useState<string>(
    reportData?.uuid ?? ""
  );
  const [, setReportTitle] = useState("Trial Report");
  const [reportInfo, setReportInfo] = useState<{
    uuid: string;
    reportId: string;
  } | null>({
    uuid: reportData?.uuid ?? "",
    reportId: reportData?.reportId ?? "",
  });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const { stateValue } = useAppState();
  const { farmId } = useTrialsRouter();
  const [internalView, setInternalView] = useState<"share" | "tree">("share");
  const [currentView, setCurrentView] = useState<{
    view: string;
    data: Report | null;
  }>({
    view: "showReport",
    data: null,
  });
  const view = drawerView ?? internalView;
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const trackReportsEvents = useTrackingEvent({
    page: TrialReportAmplitudeEvent.TRIAL_REPORTS_PAGE,
    group: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
    module: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
  });

  const handleGenerateReport = async (): Promise<void> => {
    if (!onGenerateReport) return;
    setIsGeneratingReport(true);
    try {
      void trackReportsEvents({
        event: TrialReportAmplitudeEvent.GENERATE_TRIAL_REPORT,
        originAction: "Generate Report",
      });
      const { uuid, reportId } = await onGenerateReport([trialId], fieldIds);
      setReportInfo({ uuid, reportId });
      void message.success("Report generated successfully");
    } catch (error) {
      onClose();
      void message.error("Failed to generate report");

      console.error("Report generation error:", error);
    } finally {
      setIsGeneratingReport(false);
    }
  };
  // Generate report when drawer opens if we don't have a UUID
  useEffect(() => {
    if (open && !reportInfo?.uuid && !isGeneratingReport && onGenerateReport) {
      void handleGenerateReport();
    }
  }, [open, reportInfo?.uuid, isGeneratingReport, onGenerateReport]);

  const showCreateMultipleReport = (): void => {
    setCurrentView({ view: "createReport", data: null });
  };

  const showShareReport = (data: Report): void => {
    setCurrentView({ view: "showReport", data });
  };

  const handleCreateReport = async (data: {
    field_ids: string[];
    trial_ids: string[];
  }): Promise<void> => {
    try {
      const response = await AxiosInstance.post("/online_report/generate", {
        trial_ids: data.trial_ids, // Keeping as array for backward compatibility
        field_ids: data.field_ids,
      });
      const { UUID: uuid, report_id: id } = response.data;
      showShareReport({ ...data, uuid, id });
      void trackReportsEvents({
        event: TrialReportAmplitudeEvent.CREATE_TRIAL_REPORT,
        originAction: "Create Report",
      });
      if (!uuid || !id) throw new Error("No report UUID or reportId received");
      setCurrentReportUUID(uuid);
      setInternalView("share");
    } catch (error) {
      setInternalView("tree");
      console.error("Error generating report:", error);
      throw error;
    }
  };

  const handleSendEmails = async (emails: string[]): Promise<void> => {
    try {
      await AxiosInstance.post("/online_report/share/email", {
        report_link: `${process.env.REACT_APP_REPORT_URL ?? ""}report?id=${
          reportInfo?.uuid ?? "8c2e3ecb-4611-4944-a14f-46993912c588"
        }`,
        emails,
        farm_name: stateValue.farmValue?.name ?? "",
      });
      void trackReportsEvents({
        event: TrialReportAmplitudeEvent.SEND_REPORT_EMAIL,
        originAction: "Send Email",
      });
      void message.success(formatTranslation(t("report.trial.emailSentMsg")));
    } catch (error) {
      void message.error("Failed to send emails"); // NO-SONAR
    }
  };
  if (isGeneratingReport) {
    return (
      <Drawer
        className="trial-report-drawer"
        title={
          <Flex align="center">
            <Space>
              <span>{formatTranslation(t("report.drawer.title"))}</span>
              <ExclamationCircleOutlined className="exclaim" />
            </Space>
          </Flex>
        }
        placement="right"
        width={378}
        open={open}
        onClose={onClose}
      >
        <Space
          direction="vertical"
          size={10}
          style={{ padding: "20px", width: "100%" }}
        >
          {[1, 2, 3].map((item) => (
            <Flex
              key={item}
              align="center"
              justify="space-between"
              style={{
                background: "#fff",
                borderRadius: 8,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                padding: 16,
                width: "100%",
              }}
            >
              <Skeleton
                paragraph={{ rows: 2 }}
                active
                style={{ flex: 1, marginRight: 16 }}
              />
              <Skeleton.Button active style={{ width: 80, height: 32 }} />
            </Flex>
          ))}
        </Space>
      </Drawer>
    );
  }

  return (
    <Drawer
      className="trial-report-drawer"
      title={
        <Flex align="center">
          <Space>
            <span>{formatTranslation(t("report.drawer.title"))}</span>
            <ExclamationCircleOutlined className="exclaim" />
          </Space>
        </Flex>
      }
      placement="right"
      width={378}
      open={open}
      onClose={onClose}
    >
      <>
        {view === "share" ? (
          <ShareSingleReport
            reportTitle={`${String(noOfTrails)} trials in ${String(
              noOfFields
            )} fields`}
            reportId={currentReportUUID}
            reportUUID={reportInfo?.uuid ?? ""}
            onEditTitle={(title) => {
              setReportTitle(title);
            }}
            onSendEmails={handleSendEmails}
            onViewTrialReports={() => {}}
            onBack={onEdit ?? onClose}
            showEdit={!!onEdit}
          />
        ) : (
          view === "tree" &&
          !currentView.data && (
            <CreateMultipleReport
              back={
                view === "tree"
                  ? onTreeBack ?? onClose
                  : showCreateMultipleReport
              }
              generateReport={handleCreateReport}
              propertyId={farmId ?? ""}
              checkedKeys={checkedKeys}
              setCheckedKeys={setCheckedKeys}
            />
          )
        )}
        {currentView.view === "showReport" && currentView.data && (
          <ShareReport
            uuid={currentView?.data?.uuid ?? ""}
            noOfTrails={currentView?.data?.trial_ids?.length}
            noOfFields={currentView?.data?.field_ids?.length}
            onBack={showCreateMultipleReport}
          />
        )}
      </>
    </Drawer>
  );
};

export interface CreateMultipleReportProps {
  back: () => void;
  generateReport: (data: {
    field_ids: string[];
    trial_ids: string[];
  }) => Promise<void>;
  propertyId: string;
  checkedKeys: React.Key[];
  setCheckedKeys: React.Dispatch<React.SetStateAction<React.Key[]>>;
}

export const CreateMultipleReport: React.FC<CreateMultipleReportProps> = ({
  back,
  generateReport,
  propertyId,
  checkedKeys,
  setCheckedKeys,
}) => {
  // LIFT checkedKeys state up
  // const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);

  return (
    <div>
      <TrialTreeComponent
        back={back}
        generateReport={generateReport}
        propertyId={propertyId}
        checkedKeys={checkedKeys}
        setCheckedKeys={setCheckedKeys}
      />
    </div>
  );
};

interface ShareReportProps {
  uuid: string | null;
  noOfTrails: number | undefined;
  noOfFields: number | undefined;
  onBack: () => void;
}

const ShareReport: React.FC<ShareReportProps> = ({
  uuid,
  noOfTrails,
  noOfFields,
  onBack,
}) => {
  const { stateValue } = useAppState();
  const trackReportsEvents = useTrackingEvent({
    page: TrialReportAmplitudeEvent.TRIAL_REPORTS_PAGE,
    group: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
    module: TrialReportAmplitudeEvent.TRIAL_REPORTS_GROUP,
  });
  const handleSendEmails = async (emails: string[]): Promise<void> => {
    try {
      await AxiosInstance.post("/online_report/share/email", {
        report_link: `${process.env.REACT_APP_REPORT_URL ?? ""}report?id=${
          uuid ?? "8c2e3ecb-4611-4944-a14f-46993912c588"
        }`,
        emails,
        farm_name: stateValue.farmValue?.name ?? "",
      });

      void trackReportsEvents({
        event: TrialReportAmplitudeEvent.SHARE_TRIAL_REPORT,
        originAction: "Send Email",
      });

      void message.success(formatTranslation(t("report.trial.emailSentMsg")));
    } catch (error) {
      void message.error("Failed to send emails"); // NO-SONAR
    }
  };
  return (
    <div>
      <ShareSingleReport
        reportTitle={`${String(noOfTrails)} trials in ${String(
          noOfFields
        )} fields`}
        reportUUID={uuid ?? ""}
        onSendEmails={handleSendEmails}
        onBack={onBack}
      />
    </div>
  );
};
